import React, { useCallback, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Box, Flex, Icon, IconButton, Input, Text, InputGroup, InputRightElement } from '@chakra-ui/react';
import { PiMusicNotesSimpleFill } from 'react-icons/pi';
import { BsFileEarmarkMusic } from 'react-icons/bs';
import FeatherIcon from '@ailibs/feather-react-ts';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';

type AudioPreviewProps = {
  fileUrl?: string;                     // URL do mp3 existente (edição)
  onFileChange?: (file: File | undefined) => void; // Callback ao selecionar novo arquivo
  onDelete?: () => void;                // Chama API para deletar o hino no backend
  error?: string;
};

export const AudioPreview = forwardRef<HTMLInputElement, AudioPreviewProps>(({
  fileUrl,
  onFileChange,
  onDelete,
  error
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | undefined>(undefined);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const handleSelectFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      onFileChange?.(selectedFile);
    }
  }, [onFileChange]);

  const triggerSelectFile = () => {
    inputRef.current?.click();
  };

  const handleDelete = () => {
    setFile(undefined);
    onFileChange?.(undefined);
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <InputGroup display="flex" flexDirection="column" width="100%">
      <Flex width="100%" gap={4} alignItems="center" flexWrap="wrap">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderWidth={error ? '2px' : '1px'}
          borderStyle="solid"
          borderColor={error ? 'red.500' : 'black'}
          borderRadius="8px"
          minW="85px"
          maxW="85px"
          minH="85px"
          maxH="85px"
          overflow="hidden"
        >
          <Icon as={PiMusicNotesSimpleFill} color={error ? 'red.500' : 'brand.500'} w="40px" h="40px" />
        </Box>

        <Flex flex="1" direction="column" gap={2}>
          <Input
            type="file"
            display="none"
            ref={inputRef}
            accept=".mp3, .wav"
            onChange={handleSelectFile}
          />

          <Input
            variant="flushed"
            width="100%"
            value={file?.name || fileUrl?.split('/').pop() || ''}
            readOnly
            cursor="pointer"
            borderColor={error ? 'red.500' : undefined}
            borderBottomWidth={error ? '2px' : undefined}
            onClick={triggerSelectFile}
            fontSize={12}
          />
          <InputRightElement top={0} cursor="pointer" onClick={triggerSelectFile}>
            <Icon as={BsFileEarmarkMusic} size={24} color={error ? 'red.500' : 'brand.500'} />
          </InputRightElement>

          {fileUrl && !file && (
            <AudioPlayer
              src={fileUrl}
              showSkipControls={false}
              showJumpControls={false}
              customAdditionalControls={[]}
              layout="horizontal"
            />
          )}

          {error && (
            <Text fontSize="12px" color="red.500">{error}</Text>
          )}

          <Text fontSize="12px" color="gray.500">
            Formato: MP3 ou WAV
          </Text>
        </Flex>

        <IconButton
          aria-label="Deletar áudio"
          icon={<FeatherIcon name="trash-2" size={18} />}
          color="red.500"
          variant="unstyled"
          onClick={handleDelete}
        />
      </Flex>
    </InputGroup>
  );
});
